/**
 * 부 > 류 > 호 breadcrumb.
 *
 * 예: 제8부 원피·가죽·모피 > 제42류 가죽제품 > 제4202호 케이스·가방
 *
 * 데이터:
 *  - section (1~21) — backend 가 P-pre backfill 활용해서 반환
 *  - chapter (01~97) — backend 응답
 *  - hs4 — URL param
 *  - heading_popum_ko 첫 줄 — 호명 추출
 *
 * 부 한글명은 hardcoded (21 부, WCO HS 표준).
 * 류 한글명은 CHAPTERS_97 (chaptersData.js) 활용.
 */
import React from "react";
import { ChevronRight } from "lucide-react";
import { CHAPTERS_97 } from "./chaptersData";
import { SECTION_NAMES } from "./hsHierarchyData";

function navigateToLevel({ section, chapter, hs4 }) {
  const u = new URL(window.location.href);
  u.searchParams.set("audit", "onto-detail");
  ["section", "chapter", "hs", "hsk"].forEach((k) => u.searchParams.delete(k));
  if (section) u.searchParams.set("section", String(section));
  if (chapter) u.searchParams.set("chapter", chapter);
  if (hs4) u.searchParams.set("hs", hs4);
  window.history.pushState({}, "", u.toString());
  window.dispatchEvent(new PopStateEvent("popstate"));
}

const segmentBtnStyle = {
  background: "transparent",
  border: "none",
  padding: "2px 4px",
  cursor: "pointer",
  color: "var(--audit-text-muted)",
  fontFamily: "inherit",
  fontSize: 12,
  borderRadius: 3,
  transition: "color 0.12s",
};

// chaptersData.js 의 CHAPTERS_97 에서 chapter 한글명 찾기
function chapterName(chapter) {
  if (chapter == null) return "";
  const padded = String(chapter).padStart(2, "0");
  const found = CHAPTERS_97.find((c) => c.ch === padded);
  return found ? found.name : "";
}

// 호 popum 본문에서 짧은 호명 추출
// 예: "42.02 - 트렁크·슈트 케이스·화장품 케이스·이그젝큐티브 케이스(executive case)..." → "트렁크·슈트 케이스·화장품 케이스..."
function shortHeadingName(popum) {
  if (!popum) return "";
  const firstLine = popum.split("\n")[0] || popum.slice(0, 200);
  // "42.02 - 트렁크·슈트 케이스..." → " - " 이후
  const m = firstLine.match(/[\d.]+\s*[-–·]\s*(.+)/);
  if (m) {
    // (executive case) 같은 영문 괄호 제거 후 첫 40자
    const txt = m[1].split("(")[0].trim();
    return txt.length > 40 ? txt.slice(0, 40) + "…" : txt;
  }
  return firstLine.slice(0, 40);
}

export default function HeadingBreadcrumb({
  section,
  chapter,
  hs4,
  heading_popum_ko,
  stickyRef = null,
}) {
  if (!section && !chapter && !hs4) return null;
  const sectionLabel = section && SECTION_NAMES[section];
  const chapterLabel = chapter && chapterName(chapter);
  const headingShort = shortHeadingName(heading_popum_ko);
  return (
    <div
      ref={stickyRef}
      data-testid="heading-breadcrumb"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        background: "var(--audit-bg)",
        display: "flex",
        alignItems: "center",
        gap: 4,
        fontSize: 12,
        color: "var(--audit-text-muted)",
        flexWrap: "wrap",
        padding: "10px 0",
        marginBottom: 0,
        borderBottom: "1px solid var(--audit-border)",
      }}
    >
      {section && (
        <>
          <button
            onClick={() => navigateToLevel({ section })}
            style={segmentBtnStyle}
            onMouseEnter={(e) =>
              (e.currentTarget.style.color = "var(--audit-accent)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.color = "var(--audit-text-muted)")
            }
          >
            제 <b style={{ color: "var(--audit-text)" }}>{section}</b>부{" "}
            {sectionLabel}
          </button>
          <ChevronRight size={12} />
        </>
      )}
      {chapter && (
        <>
          <button
            onClick={() => navigateToLevel({ section, chapter })}
            style={segmentBtnStyle}
            onMouseEnter={(e) =>
              (e.currentTarget.style.color = "var(--audit-accent)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.color = "var(--audit-text-muted)")
            }
          >
            제{" "}
            <b style={{ color: "var(--audit-text)" }}>
              {parseInt(chapter, 10)}
            </b>
            류 {chapterLabel}
          </button>
          <ChevronRight size={12} />
        </>
      )}
      {hs4 && (
        <button
          onClick={() => navigateToLevel({ section, chapter, hs4 })}
          style={segmentBtnStyle}
          onMouseEnter={(e) =>
            (e.currentTarget.style.color = "var(--audit-accent)")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.color = "var(--audit-text-muted)")
          }
        >
          제 <b style={{ color: "var(--audit-accent)" }}>{hs4}</b>호{" "}
          {headingShort}
        </button>
      )}
      {(() => {
        // 관세청 CLIP 007Q 원문 deep-link — 현재 레벨(호 4자리 우선, 없으면 류 2자리)을 검색.
        //   류·호 화면에서도 세번 화면처럼 관세청 원문을 바로 확인(사용자 요청).
        const srcCode =
          hs4 || (chapter != null ? String(chapter).padStart(2, "0") : null);
        if (!srcCode) return null;
        return (
          <a
            href={`https://unipass.customs.go.kr/clip/hsinfosrch/openULS0201007Q.do?searchVal=${srcCode}`}
            target="_blank"
            rel="noopener noreferrer"
            title={`관세청 CLIP 007Q 원문 — ${hs4 ? `제${hs4}호` : `제${parseInt(chapter, 10)}류`} 의 관세율·요건 (2026년 기준). 새 탭에서 관세청 데이터가 열립니다.`}
            style={{
              marginLeft: "auto",
              fontSize: 12,
              color: "var(--audit-focus, #1a73e8)",
              textDecoration: "none",
              border: "1px solid var(--audit-border)",
              borderRadius: 3,
              padding: "2px 8px",
              whiteSpace: "nowrap",
            }}
          >
            관세청 원문 확인 ↗
          </a>
        );
      })()}
      <span
        title="현재 활성 hsk 만 표시 (expr_date >= 2026-12-31). 폐기된 historical 자료는 별도 archive."
        style={{
          marginLeft: 8,
          fontSize: 12,
          color: "var(--audit-text-muted)",
          padding: "2px 8px",
          border: "1px solid var(--audit-border)",
          borderRadius: 3,
          whiteSpace: "nowrap",
        }}
      >
        2026년 기준
      </span>
    </div>
  );
}
